import { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useLazyGetListingsQuery, useUpdateListingsMutation } from 'redux/slices/globalApi';
import { setListingsCounts } from 'redux/slices/integration';

export const useListings = (type, brandUuid, enabledFilter = null) => {
    const [triggerGetListingsApi, { data, isLoading, isFetching }] = useLazyGetListingsQuery();
    const [updateListings] = useUpdateListingsMutation();
    const dispatch = useDispatch();

    const { integrationName } = useParams();

    const [searchTerm, setSearchTerm] = useState('');
    const [searchTags, setSearchTags] = useState([]);
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [fetchKey, setFetchKey] = useState(0);

    const listingRef = useRef();
    const isLoadingNextPageRef = useRef(false);
    const isInitialMountRef = useRef(true);

    const handleScroll = useCallback(() => {
        const el = listingRef?.current;
        if (!el) return;

        const { clientHeight, scrollTop, scrollHeight } = el;
        const reachedEnd = clientHeight + scrollTop >= scrollHeight - 5;

        if (
            reachedEnd &&
            !isFetching &&
            !isLoadingNextPageRef.current &&
            data?.listings?.length > 0
        ) {
            isLoadingNextPageRef.current = true;
            setPage((prev) => prev + 1);
        }
    }, [isFetching, data?.listings?.length]);

    useEffect(() => {
        if (!isFetching) {
            isLoadingNextPageRef.current = false;
        }
    }, [isFetching]);

    const fetchListings = useCallback(
        async (searchTerms, pageNum = 1) => {
            if (brandUuid === 'new') return;
            try {
                const prepareSearchTerms = (searchTerms || []).map((item) => item.label);

                const enabled =
                    typeof enabledFilter === 'boolean' || enabledFilter === null
                        ? enabledFilter
                        : null;

                const query = {
                    page: pageNum,
                    payload: {
                        searchTerms: prepareSearchTerms,
                        brandUuid: brandUuid ?? null,
                        ignoreCurrentBrand: type === 'move',
                        enabled,
                    },
                    integrationName,
                };
                const result = await triggerGetListingsApi(query).unwrap();

                if (pageNum === 1 && prepareSearchTerms.length === 0 && enabled === null) {
                    dispatch(
                        setListingsCounts({
                            totalListingsCount: result?.total ?? 0,
                            totalDisabledListings: result?.disabled ?? 0,
                        })
                    );
                }

                const newRows = (result?.listings || []).map((item) => ({
                    id: item.uuid,
                    brand: item.brandName,
                    name: item.name,
                    address: item.address,
                    enabled: item.enabled,
                }));
                setRows(pageNum === 1 ? newRows : (prev) => [...prev, ...newRows]);
            } catch (err) {}
        },
        [brandUuid, type, integrationName, triggerGetListingsApi, enabledFilter, dispatch]
    );

    const fetchRef = useRef(fetchListings);
    fetchRef.current = fetchListings;

    const onSubmit = useCallback(
        (searchTerms) => {
            const prepareSearchTerms = (searchTerms || []).map((item) => item.label);
            setPage(1);

            if (prepareSearchTerms.length === 0) {
                setRows([]);
            }
            if (listingRef.current) {
                listingRef.current.scrollTop = 0;
            }
        },
        [listingRef]
    );

    const refreshListings = useCallback(() => {
        setPage(1);
        setFetchKey((k) => k + 1);
    }, []);

    // When the enabled filter changes after the initial mount, reset pagination,
    // clear stale rows, scroll to top, and bump fetchKey to guarantee a re-fetch.
    useEffect(() => {
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            return;
        }
        setPage(1);
        setRows([]);
        setFetchKey((k) => k + 1);
        if (listingRef.current) {
            listingRef.current.scrollTop = 0;
        }
    }, [enabledFilter]);

    // Single fetch effect driven by page, searchTags, and fetchKey.
    // Uses fetchRef so it always calls the latest fetchListings without
    // depending on its identity (which would race with setPage on filter changes).
    useEffect(() => {
        fetchRef.current(searchTags, page);
    }, [page, searchTags, fetchKey]);

    useEffect(() => {
        if (searchTags.length === 0) {
            setRows([]);
            setPage(1);
        }
    }, [searchTags]);

    const onRemoveSearchTag = async (id) => {
        const updatedTags = searchTags.filter((tag) => tag.id !== id);
        setSearchTags(updatedTags);
        setPage(1);
        if (listingRef.current) {
            listingRef.current.scrollTop = 0;
        }
    };

    const onSaveChanges = useCallback(
        async (payload) => {
            await updateListings({ payload, integrationName }).unwrap();
        },
        [integrationName, updateListings]
    );

    return {
        listingRef,
        handleScroll,
        isLoading,
        isFetching,
        rows,
        setRows,
        searchTerm,
        setSearchTerm,
        searchTags,
        setSearchTags,
        onSubmit,
        onRemoveSearchTag,
        refreshListings,
        onSaveChanges,
        totalCount: data?.total,
        currentBrandListings: data?.currentBrandListings,
        page,
    };
};
